import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { moeda } from '../../../components/funcoes';
import { Table, Whisper, Tooltip as HelpTooltip, InputGroup } from 'rsuite';
import { useAuth } from '../../../hooks/auth';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { CustomHeaderCell } from '../../../components/CustomHeaderCell';
import { CustomCell } from '../../../components/CustomCell';
import { RelatorioFiltroPeriodo } from '../../../components/RelatorioFiltroPeriodo';
import { CustomInput } from '../../../components/CustomInput';
import { FaInfoCircle } from 'react-icons/fa';
import Chart from 'react-google-charts';
import {
  RelatorioPeriodoProdutos,
  DashboardRow,
  KpiCard,
  KpiLabel,
  KpiValue,
  PageWrapper,
  PageHeader,
  PageTitle,
  SearchWrapper,
  TableColumn,
  ContentRow,
  ChartsColumn,
  ChartCard,
  ChartTitleRow,
  LegendRow,
  LegendItem,
  LegendDot,
  SearchIcon,
} from './styles';

const { Column } = Table;

const ConsultarProdutos = () => {
  const { id_empresa } = useAuth();
  const [vendas, setVendas] = useState([]);
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);
  const [loading, setLoading] = useState(false);
  const { theme } = useCustomTheme();
  const [busca, setBusca] = useState('');

  const filtrar = useCallback(async () => {
    setLoading(true);
    let inicial = null,
      final = null;
    if (dataInicio && dataFim) {
      inicial = dataInicio;
      final = dataFim;
    } else {
      let data = new Date();
      let primeiroDia = new Date(data.getFullYear(), data.getMonth(), 1);
      let ultimoDia = new Date(data.getFullYear(), data.getMonth() + 1, 0);
      inicial =
        primeiroDia.getFullYear() +
        '-' +
        ('00' + (primeiroDia.getMonth() + 1)).slice(-2) +
        '-' +
        ('00' + primeiroDia.getDate()).slice(-2);
      final =
        ultimoDia.getFullYear() +
        '-' +
        ('00' + (ultimoDia.getMonth() + 1)).slice(-2) +
        '-' +
        ('00' + ultimoDia.getDate()).slice(-2);
    }
    setDataInicio(inicial);
    setDataFim(final);
    await api
      .post('pedido/relatoriosGeral/consultarProdutos', {
        IdEmpresa: id_empresa,
        DataInicial: inicial,
        DataFinal: final,
      })
      .then((res) => {
        if (res.data.length > 0) {
          setVendas(res.data);
        }
        if (res.data.length === 0) {
          setVendas([]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dataFim, dataInicio, id_empresa]);

  const dashboard = useMemo(() => {
    const itensVendidos = vendas.reduce(
      (sum, v) => sum + Number(v.QuantidadeVendida),
      0,
    );

    const faturamento = vendas.reduce((sum, v) => sum + Number(v.Valor), 0);
    const maisVendido = vendas[0]?.Descricao ?? '-';

    return {
      produtosVendidos: vendas.length,
      itensVendidos,
      faturamento,
      maisVendido,
    };
  }, [vendas]);

  const paraTitulo = (texto) =>
    texto.toLowerCase().replace(/(^|\s)\S/g, (letra) => letra.toUpperCase());

  const topProdutos = useMemo(
    () =>
      vendas.slice(0, 5).map((v) => ({
        ...v,
        Descricao: paraTitulo(v.Descricao),
        QuantidadeVendida: Number(v.QuantidadeVendida),
      })),
    [vendas],
  );

  const dadosRosca = useMemo(() => {
    const top3 = vendas.slice(0, 3);
    const restoQtd = vendas
      .slice(3)
      .reduce((sum, v) => sum + Number(v.QuantidadeVendida), 0);

    const cores = ['#2a78d6', '#eb6834', '#1baf7a'];
    const segmentos = top3.map((v, i) => ({
      name: paraTitulo(v.Descricao),
      value: Number(v.QuantidadeVendida),
      color: cores[i],
    }));

    if (restoQtd > 0) {
      segmentos.push({ name: 'Demais', value: restoQtd, color: '#898781' });
    }

    return segmentos;
  }, [vendas]);

  const dadosBarras = useMemo(
    () => [
      ['Produto', 'Quantidade', { role: 'annotation' }],
      ...topProdutos.map((p) => [
        p.Descricao,
        p.QuantidadeVendida,
        String(p.QuantidadeVendida),
      ]),
    ],
    [topProdutos],
  );

  const dadosGraficoRosca = useMemo(
    () => [
      ['Produto', 'Quantidade'],
      ...dadosRosca.map((segmento) => [segmento.name, segmento.value]),
    ],
    [dadosRosca],
  );

  const produtosFiltrados = useMemo(() => {
    return vendas.filter((v) =>
      v.Descricao.toLowerCase().includes(busca.toLowerCase()),
    );
  }, [vendas, busca]);

  useEffect(() => {
    filtrar();
  }, [filtrar]);

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Consultar Produtos</PageTitle>
        <RelatorioPeriodoProdutos>
          <RelatorioFiltroPeriodo
            dataInicio={dataInicio}
            dataFim={dataFim}
            onChangeDataInicio={setDataInicio}
            onChangeDataFim={setDataFim}
          />
        </RelatorioPeriodoProdutos>
        <SearchWrapper>
          <InputGroup inside style={{ border: 'none' }}>
            <CustomInput
              placeholder="Buscar produto..."
              value={busca}
              onChange={setBusca}
            />
            <InputGroup.Addon>
              <SearchIcon size={14} $spinning={loading} />
            </InputGroup.Addon>
          </InputGroup>
        </SearchWrapper>
      </PageHeader>

      <DashboardRow>
        <KpiCard>
          <KpiLabel>Produtos vendidos</KpiLabel>
          <KpiValue>{dashboard.produtosVendidos}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Faturamento</KpiLabel>
          <KpiValue>{moeda(dashboard.faturamento)}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Itens vendidos</KpiLabel>
          <KpiValue>{dashboard.itensVendidos}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Mais vendido</KpiLabel>
          <KpiValue>{dashboard.maisVendido}</KpiValue>
        </KpiCard>
      </DashboardRow>

      <ContentRow>
        <TableColumn>
          <Table
            height={window.innerHeight - 200}
            data={produtosFiltrados}
            bordered={true}
            loading={loading}
            style={{
              borderColor: theme.colors.backgroundSecondary,
              backgroundColor: theme.colors.backgroundSecondary,
              color: theme.colors.textSecondary,
              borderRadius: '10px',
            }}
            locale={{
              emptyMessage: 'dados não encontrados',
              loading: 'Carregando...',
            }}
          >
            <Column
              style={{
                background: theme.colors.backgroundSecondary,
                color: theme.colors.textSecondary,
              }}
              flexGrow={1}
              fixed
            >
              <CustomHeaderCell>Descrição</CustomHeaderCell>
              <CustomCell dataKey="Descricao" />
            </Column>

            <Column
              style={{
                background: theme.colors.backgroundSecondary,
                color: theme.colors.textSecondary,
              }}
              flexGrow={1}
            >
              <CustomHeaderCell>Qtd. Vendida</CustomHeaderCell>
              <CustomCell>
                {(rowData) => <text>{+rowData.QuantidadeVendida}</text>}
              </CustomCell>
            </Column>

            <Column
              style={{
                background: theme.colors.backgroundSecondary,
                color: theme.colors.textSecondary,
              }}
            >
              <CustomHeaderCell>Valor</CustomHeaderCell>
              <CustomCell>
                {(rowData) => <text>{moeda(rowData.Valor)}</text>}
              </CustomCell>
            </Column>
          </Table>
        </TableColumn>

        <ChartsColumn>
          <ChartCard>
            <ChartTitleRow>
              <KpiLabel>Produtos mais vendidos</KpiLabel>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={
                  <HelpTooltip>
                    Os produtos com mais unidades vendidas no período
                    selecionado.
                  </HelpTooltip>
                }
              >
                <span style={{ cursor: 'help', display: 'flex' }}>
                  <FaInfoCircle size={12} color={theme.colors.textSecondary} />
                </span>
              </Whisper>
            </ChartTitleRow>
            {topProdutos.length > 0 && (
              <Chart
                chartLanguage="pt"
                chartType="BarChart"
                width="100%"
                height="180px"
                loader={<div>Carregando gráfico</div>}
                data={dadosBarras}
                options={{
                  colors: ['#2a78d6'],
                  legend: 'none',
                  backgroundColor: 'transparent',
                  hAxis: {
                    textPosition: 'none',
                    gridlines: { count: 0 },
                    baselineColor: 'transparent',
                    minValue: 0,
                  },
                  vAxis: {
                    textStyle: {
                      color: theme.colors.textSecondary,
                      fontSize: 11,
                    },
                  },
                  annotations: {
                    textStyle: { color: theme.colors.textSecondary },
                  },
                  bar: { groupWidth: '65%' },
                  chartArea: { left: 120, right: 24, top: 8, bottom: 8 },
                }}
              />
            )}
          </ChartCard>

          <ChartCard>
            <ChartTitleRow>
              <KpiLabel>Top 3 produtos vs Demais</KpiLabel>
              <Whisper
                placement="top"
                trigger="hover"
                speaker={
                  <HelpTooltip>
                    Participação dos 3 produtos mais vendidos e do restante do
                    cardápio, em quantidade vendida.
                  </HelpTooltip>
                }
              >
                <span style={{ cursor: 'help', display: 'flex' }}>
                  <FaInfoCircle size={12} color={theme.colors.textSecondary} />
                </span>
              </Whisper>
            </ChartTitleRow>
            {dadosRosca.length > 0 && (
              <Chart
                chartLanguage="pt"
                chartType="PieChart"
                width="100%"
                height="220px"
                loader={<div>Carregando gráfico</div>}
                data={dadosGraficoRosca}
                options={{
                  pieHole: 0.4,
                  colors: dadosRosca.map((segmento) => segmento.color),
                  legend: 'none',
                  backgroundColor: 'transparent',
                  pieSliceText: 'percentage',
                  pieSliceTextStyle: {
                    color: '#fff',
                    fontSize: 12,
                    bold: true,
                  },
                  pieSliceBorderColor: 'transparent',
                  chartArea: { width: '90%', height: '90%' },
                }}
              />
            )}
            <LegendRow>
              {dadosRosca.map((d) => (
                <LegendItem key={d.name}>
                  <LegendDot $color={d.color} />
                  {d.name}
                </LegendItem>
              ))}
            </LegendRow>
          </ChartCard>
        </ChartsColumn>
      </ContentRow>
    </PageWrapper>
  );
};

export default ConsultarProdutos;
